import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X, LogOut, Shield } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';

const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const isAuth = localStorage.getItem('isAdminAuthenticated');
    const userData = localStorage.getItem('adminUser');
    
    // If authenticated but no user data, set a default admin user
    if (isAuth === 'true' && !userData) {
      const defaultAdmin = { name: 'Admin', email: 'admin@netixfibernet.com' };
      localStorage.setItem('adminUser', JSON.stringify(defaultAdmin));
      setAdminUser(defaultAdmin);
    } else if (userData) {
      setAdminUser(JSON.parse(userData));
    }
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('adminUser');
    setAdminUser(null);
    toast.success('Admin logged out successfully');
    navigate('/admin/login');
  };

  return (
    <nav className="bg-gray-900 shadow-md sticky top-0 z-50 border-b-4 border-red-400">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/admin/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)'
            }}>
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-lg">Netix Admin</p>
              <p className="text-gray-400 text-xs">Management Panel</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <Link 
              to="/admin/dashboard" 
              className="text-gray-300 hover:text-white font-medium transition-colors"
            >
              Dashboard
            </Link>
            <Link 
              to="/admin/orders" 
              className="text-gray-300 hover:text-white font-medium transition-colors"
            >
              Orders
            </Link>
            <Link 
              to="/admin/users" 
              className="text-gray-300 hover:text-white font-medium transition-colors"
            >
              Users
            </Link>
            <Link 
              to="/admin/commissions" 
              className="text-gray-300 hover:text-white font-medium transition-colors"
            >
              Commissions
            </Link>
            <Link 
              to="/admin/messages" 
              className="text-gray-300 hover:text-white font-medium transition-colors"
            >
              Messages
            </Link>
            <div className="flex items-center gap-4 ml-4">
              <NotificationBell />
              {adminUser && (
                <span className="text-gray-300 font-medium">{adminUser.name}</span>
              )}
              <Button 
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white border-2 border-red-400"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-800">
            <div className="flex flex-col gap-4">
              <Link 
                to="/admin/dashboard" 
                className="text-gray-300 hover:text-white font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link 
                to="/admin/orders" 
                className="text-gray-300 hover:text-white font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Orders
              </Link>
              <Link 
                to="/admin/users" 
                className="text-gray-300 hover:text-white font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Users
              </Link>
              <Link 
                to="/admin/commissions" 
                className="text-gray-300 hover:text-white font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Commissions
              </Link>
              <Link 
                to="/admin/messages" 
                className="text-gray-300 hover:text-white font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Messages
              </Link>
              {adminUser && (
                <span className="text-gray-300 font-medium py-2">{adminUser.name}</span>
              )}
              <Button 
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="bg-red-600 hover:bg-red-700 text-white border-2 border-red-400"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default AdminNavbar;
